import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Receipt, Trash2, RefreshCw, ExternalLink, Download, Loader2 } from "lucide-react";
import { useAppearance } from "../contexts/AppearanceContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  PENDING: { label: "Pendente", bg: "bg-amber-100 text-amber-700" },
  RECEIVED: { label: "Pago", bg: "bg-emerald-100 text-emerald-700" },
  CONFIRMED: { label: "Confirmado", bg: "bg-emerald-100 text-emerald-700" },
  RECEIVED_IN_CASH: { label: "Pago", bg: "bg-emerald-100 text-emerald-700" },
  OVERDUE: { label: "Vencido", bg: "bg-rose-100 text-rose-700" },
  REFUNDED: { label: "Estornado", bg: "bg-slate-200 text-slate-600" },
  CANCELLED: { label: "Cancelado", bg: "bg-slate-200 text-slate-600" },
};

const TYPE_LABELS = { BOLETO: "Boleto", PIX: "Pix", CREDIT_CARD: "Cartão" };

export default function BillingDetailModal({ billing, open, onClose, onRefresh }) {
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";
  const tc50 = appearance.colors?.["50"] || "#EFF6FF";

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [localBilling, setLocalBilling] = useState(null);

  const b = localBilling || billing;
  if (!b) return null;
  const st = STATUS_MAP[b.status] || STATUS_MAP.PENDING;
  const isPaid = ["RECEIVED", "CONFIRMED", "RECEIVED_IN_CASH"].includes(b.status);
  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const handleRefresh = async () => {
    try {
      const r = await axios.post(`${API}/asaas/billings/${b.id}/refresh-status`);
      if (r.data.ok) {
        setLocalBilling(prev => ({ ...(prev || b), status: r.data.status, invoice_url: r.data.invoice_url || (prev || b).invoice_url }));
        if (onRefresh) onRefresh();
        toast.success("Atualizado!");
      }
    } catch { toast.error("Erro"); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/asaas/billings/${b.id}`);
      toast.success("Cobranca cancelada!");
      setDeleteOpen(false);
      onClose();
      if (onRefresh) onRefresh();
    } catch { toast.error("Erro"); }
  };

  return (<>
    <Dialog open={open} onOpenChange={v => { if (!v) { setLocalBilling(null); onClose(); } }}>
      <DialogContent className="max-w-lg !p-0 overflow-hidden" data-testid="billing-detail-modal">
        <div className="px-6 pt-5 pb-1">
          <p className="font-heading font-bold text-lg text-slate-900 flex items-center gap-2">
            <Receipt className="w-5 h-5" style={{ color: tc500 }} />{b.description || "Cobrança"}
          </p>
        </div>
        <div className="px-6 pb-6 space-y-3">
          {/* Deal card */}
          <div className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg">
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: tc50 }}>
              <Receipt className="w-4 h-4" style={{ color: tc500 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-800">{b.negocio_titulo || b.description || "—"}</p>
              <p className="text-[11px] text-slate-400">{TYPE_LABELS[b.billing_type] || b.billing_type}</p>
            </div>
            <Badge className={`${st.bg} border-0 text-[9px]`}>{st.label}</Badge>
          </div>

          {/* Info */}
          <div className="flex items-center gap-2 text-[11px] text-slate-400">
            <span>Emitido em {new Date(b.created_at).toLocaleDateString("pt-BR")} às {new Date(b.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>
            <span>·</span>
            <span>{b.created_by_name}</span>
          </div>

          {/* Value */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200" style={{backgroundColor:"#fff"}}>
            <div>
              <p className="text-xs text-slate-500">Valor</p>
              <p className="text-xl font-bold text-slate-900">{fmt(b.value)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-500">Vencimento</p>
              <p className="text-sm font-medium text-slate-800">{b.due_date ? new Date(b.due_date + "T12:00:00").toLocaleDateString("pt-BR") : "—"}</p>
            </div>
          </div>

          {b.installment_count > 1 && (
            <p className="text-xs text-slate-500">{b.installment_count}x de {fmt(b.value / b.installment_count)}</p>
          )}

          {/* Actions */}
          {b.status !== "CANCELLED" && b.status !== "DELETED" && (
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" className="h-8 w-8 p-0 bg-rose-600 hover:bg-rose-700 text-white border-0" onClick={() => setDeleteOpen(true)} title="Cancelar">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
            <div className="flex-1" />
            {!isPaid && (
              <Button variant="outline" size="sm" className="h-8 w-8 p-0 bg-white text-slate-500 border-slate-300 hover:bg-slate-50" onClick={handleRefresh} title="Atualizar">
                <RefreshCw className="w-3.5 h-3.5" />
              </Button>
            )}
            {b.invoice_url && (
              <a href={b.invoice_url} target="_blank" rel="noreferrer">
                <Button size="sm" className="h-8 px-3 text-xs gap-1.5 text-white" style={{ backgroundColor: tc500 }}>
                  <ExternalLink className="w-3.5 h-3.5" />{isPaid ? "Ver comprovante" : "Link de pagamento"}
                </Button>
              </a>
            )}
          </div>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-rose-600"><Trash2 className="w-5 h-5" />Cancelar cobrança?</AlertDialogTitle>
          <AlertDialogDescription>A cobrança será cancelada no Asaas e não poderá mais ser paga.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Voltar</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700 text-white" onClick={handleDelete}>Cancelar cobrança</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>);
}
